/**
 * PlayingNowSection — everyone live in this title's category.
 *
 * The counterpart to the Official Creator Team, and deliberately NOT it.
 *
 * The roster is people the publisher works with; being on it is an endorsement
 * they granted. This is everyone the platform says is in the category right
 * now — the same population a Twitch category page shows, which anybody joins
 * by launching the game.
 *
 * That difference is the entire reason this component exists, so it is stated
 * on the surface rather than left for a visitor to infer: the heading says
 * "playing now", the note says these are not official, and the cards are
 * visually lighter than a roster card so the two rows never read as one list
 * with a divider in it.
 */

import React from "react";
import {useGetDropPlaying} from "@/hooks/drop/useGetDropPlaying";
import {formatCompactNumber} from "@/utils/formatCompactNumber";
import {safeCssUrl} from "./play/safeHref";
import "./PlayingNowSection.scss";

interface PlayingNowSectionProps {
    /** The title whose category defines "this game". */
    slug?: string;
    /** How many to show. The rest are on the platform's own category page. */
    limit?: number;
}

export function PlayingNowSection({slug, limit = 12}: PlayingNowSectionProps): React.ReactElement | null {
    const {data} = useGetDropPlaying(slug, limit);

    const creators = data?.creators ?? [];

    /*
     * Nothing to say ⇒ say nothing.
     *
     * Both the unconfigured case (no categories set on the release) and the
     * quiet case (nobody live) render as absent. They are different facts and
     * the API distinguishes them, but neither is worth a block on a public
     * page — an operator finds the first in the editor, not here.
     */
    if (!creators.length) return null;

    return (
        <section className="playing-now" aria-label="Playing now">
            <div className="playing-now__head">
                <h2 className="playing-now__title">Playing now</h2>
                {/*
                  * The disclaimer is not fine print. Without it this row sits
                  * under "Official Creator Team" on a publisher's own page and
                  * reads as more of the same — which would put people the
                  * publisher has never met under their endorsement.
                  */}
                <span className="playing-now__note">
                    Live in this category · not official creators
                </span>
            </div>

            <div className="playing-now__grid">
                {creators.map(c =>
                    <a
                        key={c.slug}
                        className="playing-now__card"
                        href={`/drop-in/${c.slug}`}
                    >
                        {c.thumbnailUrl &&
                            <span
                                className="playing-now__art"
                                style={{backgroundImage: safeCssUrl(c.thumbnailUrl)}}
                                aria-hidden="true"
                            />
                        }
                        <span className="playing-now__body">
                            <span className="playing-now__name">{c.displayName}</span>
                            {c.streamTitle &&
                                <span className="playing-now__stream">{c.streamTitle}</span>
                            }
                        </span>
                        {c.viewerCount !== undefined && c.viewerCount > 0 &&
                            <span className="playing-now__viewers">
                                {formatCompactNumber(c.viewerCount)}
                            </span>
                        }
                    </a>)}
            </div>
        </section>
    );
}
